/* eslint-disable sort-keys-fix/sort-keys-fix -- CSS-in-JS pseudo-selector keys use semantic ordering */
// MUI v4 Chip — NS/Tractie Chip component overrides.
//
// Architecture: two-layer override system.
//
//   Layer 1 (Tag-base, `&` default): MuiChip used as static Tag → maps to
//     Tractie Tag spec. Re-exports MuiChipLightOverrides from
//     mui-chip-as-tag-overrides.ts (the Tag-role mapping remains unchanged).
//
//   Layer 2 (Chip-modifier, `&.MuiChip-clickable, &.MuiChip-deletable`):
//     MuiChip with onClick/onDelete props → MUI auto-adds these classes.
//     We override to Tractie Chip spec: 6px radius, 14px font, 32px height,
//     bold weight, border-emphasis, hover/pressed/selected states.
//
// MUI voegt deze class-suffixes automatisch toe op basis van props:
//   - onClick present → MuiChip-clickable
//   - onDelete present → MuiChip-deletable
//
// Geen story-data-attributes nodig voor het basisonderscheid Tag vs Chip.
// Specifieke emphasis-state via data-tractie-appearance="emphasis" (zie
// Cluster C5 in chip.comparison.stories.tsx).
//
// transition: none op root + clickable surfaces (ground-rules.md "Motion and text": geen animatie).

export {
  MuiChipDarkOverrides,
  MuiChipLightOverrides,
} from '../tag/mui-chip-as-tag-overrides';

// ────────────────────────────────────────────────────────────────────────────
// Chip-modifier overrides — applied on top of the Tag-base for interactive
// MUI Chips (those with onClick or onDelete handlers).
//
// These extend the MuiChipLightOverrides root object with additional selectors
// targeting the auto-applied MUI classes.
// ────────────────────────────────────────────────────────────────────────────

/**
 * Chip-modifier root extensions.
 *
 * Spread into `MuiChip.root` alongside MuiChipLightOverrides.root to activate
 * the Tractie Chip spec for interactive chips:
 *   - border-radius: 6px (radius-m, vs Tag's 16px pill)
 *   - font-size: 14px (0.875rem, vs Tag's 12px)
 *   - font-weight: 700 (bold)
 *   - height: 32px min-height (vs Tag's auto)
 *   - padding: spacer-2/spacer-3 = 8px/12px
 *   - border: 1px solid color-border-emphasis
 *   - hover/pressed/selected/focus states per Tractie Chip spec
 */
export const MuiChipInteractiveRootExtension = {
  // ── Chip-role: MUI auto-adds MuiChip-clickable / MuiChip-deletable ──────
  // These selectors override the Tag-base (16px radius etc.) when onClick or
  // onDelete is present. Specificity: the nested & + class selector wins over
  // the plain & root — JSS processes these as compound selectors.
  '&$clickable, &$deletable': {
    borderRadius: '6px',
    fontSize: '0.875rem',
    fontWeight: 700,
    // Explicit 32px height to match Tractie Chip spec.
    // height: 'auto' + padding: '8px 12px' resulted in 38-40px due to
    // MUI default padding-block compounding with inline-flex line-height.
    // paddingBlock 0 prevents compound; horizontal padding stays 12px.
    height: '32px',
    minHeight: '32px',
    padding: '0 12px',
    paddingLeft: '12px',
    paddingRight: '12px',
    border: '1px solid var(--ns-color-border-emphasis, var(--bui-border-2))',
    backgroundColor: 'transparent',
    color: 'var(--ns-color-fg-emphasis, var(--bui-fg-primary))',
    cursor: 'pointer',
    transition: 'none',
  },

  // Hover state — Tractie Chip hover: color-background-hover.
  // Force-class variant (foundation T1.3) allows comparison-grid stories to
  // visually render the hover-row by setting className="force-hover" on the
  // MuiChip element. See packages/storybook-utils/src/comparison/force-state.ts.
  '&$clickable:hover:not($disabled), &$deletable:hover:not($disabled), &$clickable.force-hover:not($disabled), &$deletable.force-hover:not($disabled)':
    {
      backgroundColor: 'var(--ns-color-bg-hover)',
    },

  // Active/pressed state — Tractie Chip active: color-background-pressed.
  // Force-class variant mirrors the hover pattern (see above).
  '&$clickable:active:not($disabled), &$deletable:active:not($disabled), &$clickable.force-active:not($disabled), &$deletable.force-active:not($disabled)':
    {
      backgroundColor: 'var(--ns-color-bg-pressed)',
    },

  // Focus-visible state — Tractie button focus formula: +1px offset outside.
  // Force-class variant is `Mui-focusVisible` (MUI v4 native state-class), not
  // `force-focus-visible` — stories add `className="Mui-focusVisible"` to
  // activate. force-focus-visible is also accepted for layer-uniformity with
  // BUI/NS so the story-helper can return one class for all libraries.
  '&$clickable:focus-visible, &$deletable:focus-visible, &$clickable.force-focus-visible, &$deletable.force-focus-visible':
    {
      outline: '2px solid var(--ns-color-border-focus)',
      outlineOffset: '2px',
    },

  // ── Selected/checked state via data-tractie-checked attribute ──────────
  // Filter-chips: checked state uses selected-default tokens.
  // Activated by data-tractie-checked="" attribute (boolean pattern).
  //
  // Checked ✓ glyph: Tractie renders a tractie-icon[name="check"] SVG
  // (16×16, stroke-width thick) as first child of the button when checked.
  // MUI has no native checkmark; we inject it via .MuiChip-label::before.
  // The ✓ glyph (U+2713) matches Tractie's check icon visually.
  // font-weight: 700 + font-size inherit → matches chip bold text weight.
  '&$clickable[data-tractie-checked]': {
    // Tractie checked: border-color = bg-color (same blue-subtle) → onzichtbare border.
    // We hadden border-info (dark blue) wat MUI/NS visueel deed afwijken van Tractie.
    borderColor:
      'var(--ns-color-bg-selected-default, var(--ns-color-bg-info-subtle))',
    backgroundColor:
      'var(--ns-color-bg-selected-default, var(--ns-color-bg-info-subtle))',
    '& .MuiChip-label::before': {
      content: '"✓"',
      marginRight: '4px',
      fontWeight: 700,
      lineHeight: 1,
    },
  },

  '&$clickable[data-tractie-checked]:hover:not($disabled), &$clickable[data-tractie-checked].force-hover:not($disabled)':
    {
      backgroundColor:
        'var(--ns-color-bg-selected-hover, var(--ns-color-bg-hover))',
    },

  // Checked + active — Tractie selected-pressed token (solid light-blue, niet
  // alpha-overlay zoals base &$clickable:active). Eerder ontbrak deze regel
  // waardoor MUI checked+active terugviel naar base :active alpha-blue terwijl
  // NS chip-selected.force-active al de juiste selected-pressed token toonde.
  // Bart-feedback 2026-05-21 T1.5 gap-scan.
  '&$clickable[data-tractie-checked]:active:not($disabled), &$clickable[data-tractie-checked].force-active:not($disabled)':
    {
      backgroundColor:
        'var(--ns-color-bg-selected-pressed, var(--ns-color-bg-pressed))',
    },

  // ── Checked: hide leading-icon (Tractie-spirit) ────────────────────────
  // Tractie behaviour: when filter is checked, the leading icon is REPLACED
  // by the ✓ checkmark — only ✓ + label visible. MUI default renders both
  // icon AND ✓ glyph, creating `Q ✓ filter` instead of `✓ filter`.
  // Hide .MuiChip-icon when checked to match Tractie.
  '&$clickable[data-tractie-checked] .MuiChip-icon': {
    display: 'none',
  },

  // ── Disabled state — Tractie-spirit opacity 0.4 ────────────────────────
  // Base Tag layer sets &$disabled to opacity 0.5 (MUI default-ish).
  // Interactive Chip spec uses 0.4 per Tractie principles.md disabled-anatomy.
  // This more-specific compound selector wins over the root &$disabled rule.
  '&$clickable$disabled, &$deletable$disabled': {
    opacity: 0.4,
    pointerEvents: 'none' as const,
  },

  // ── MUI icon gap fix ───────────────────────────────────────────────────
  // MUI default: .MuiChip-icon has marginRight: -8px (negative), causing
  // the icon to overlap the label text ("🔍chip" instead of "🔍 chip").
  // Tractie uses column-gap: 4px on the button element; we replicate by
  // setting marginRight: 4px (spacer-1) and marginLeft: 0 on the icon.
  // The root already has no padding compensation because paddingLeft:7px
  // (set below) accounts for the icon-leading padding reduction.
  '&$clickable .MuiChip-icon, &$deletable .MuiChip-icon': {
    marginRight: '4px',
    marginLeft: '0',
    color: 'inherit',
  },

  // ── Icon-leading padding reduction ─────────────────────────────────────
  // Tractie chip.css.js:154-158: when button has tractie-icon:first-child,
  // padding-inline-start -= spacer-1 (4px). This yields start = 11-4 = 7px.
  // MUI icon prop renders .MuiChip-icon as first child of the root.
  // Selector: compound &$clickable with :has(.MuiChip-icon) — CSS :has() is
  // available in Chromium 105+ (Backstage targets modern browsers).
  '&$clickable:has(.MuiChip-icon), &$deletable:has(.MuiChip-icon)': {
    paddingLeft: '7px',
  },

  // ── Deletable: delete-icon side padding reduction ────────────────────────
  // Tractie chip.css.js:161-164: when button has tractie-icon:last-child,
  // padding-inline-end -= spacer-1 (4px). This yields end = 11-4 = 7px.
  // MUI deletable chips render .MuiChip-deleteIcon as last child.
  // Override the base 12px end padding to 7px for deletable chips.
  '&$deletable': {
    paddingRight: '7px',
  },

  // ── Delete icon (dismiss ×) — Tractie ChipDismissible style ────────────
  // MUI default deleteIcon is CancelIcon SVG: 22px circle-with-× design.
  // Tractie/NS use a plain 16×16 × icon without circle background.
  // Override: shrink to 16×16 and set color to inherit for plain × look.
  // The SVG path itself draws the circle — we cannot remove it via CSS,
  // but at 16px scale the circle reads as a compact × matching Tractie.
  '&$deletable .MuiChip-deleteIcon': {
    width: '16px',
    height: '16px',
    fontSize: '14px',
    fontWeight: 400,
    color: 'currentColor',
    opacity: 1,
    flexShrink: 0,
    // Add gap between label and × glyph (matches Tractie column-gap spacer-1).
    marginLeft: '4px',
    marginRight: '0',
    // Match Tractie's lighter × stroke — bold 700 ziet er dikker uit.
    lineHeight: 1,
  },

  // ── Dismissible (deletable) full padding — Tractie ChipDismissible spec ──
  // Tractie modifiers layer: padding-block 0, start=spacer-2-border=7px,
  // end=spacer-1-border=3px. This is handled by overriding the base padding
  // set above (0 12px becomes: block 0 already from height:32px + no padding-
  // block; start 7px from deletable rule; end now needs 3px).
  // MUI deletable with delete-icon: end reduces further to 3px.
  '&$deletable:has(.MuiChip-deleteIcon)': {
    paddingLeft: '7px',
    paddingRight: '3px',
  },

  // ── Selected emphasis state (appearance=emphasis + checked) ────────────
  // Tractie selected-emphasis tokens: inverted fill with emphasis bg.
  // Activated by data-tractie-appearance="emphasis" on checked chips.
  '&$clickable[data-tractie-checked][data-tractie-appearance="emphasis"]': {
    borderColor: 'transparent',
    backgroundColor:
      'var(--ns-color-bg-selected-emphasis-default, var(--ns-color-bg-interactive-default))',
    color: 'var(--ns-color-fg-default-inverse, #fff)',
  },

  '&$clickable[data-tractie-checked][data-tractie-appearance="emphasis"]:hover:not($disabled), &$clickable[data-tractie-checked][data-tractie-appearance="emphasis"].force-hover:not($disabled)':
    {
      backgroundColor:
        'var(--ns-color-bg-selected-emphasis-hover, var(--ns-color-bg-interactive-hover))',
    },

  // Checked emphasis + active — Tractie selected-emphasis-pressed (darker blue).
  // Spiegel van non-emphasis checked-active. Eerder ontbrak regel waardoor MUI
  // emphasis+active terugviel naar lichter interactive-default. T1.5 gap-scan.
  '&$clickable[data-tractie-checked][data-tractie-appearance="emphasis"]:active:not($disabled), &$clickable[data-tractie-checked][data-tractie-appearance="emphasis"].force-active:not($disabled)':
    {
      backgroundColor:
        'var(--ns-color-bg-selected-emphasis-pressed, var(--ns-color-bg-interactive-pressed))',
    },
} as const;

/**
 * MUI Chip label padding reset for interactive Chip-role.
 * Tractie Chip spec: all horizontal space from root padding (12px).
 * Override MUI's MuiChip-label inner padding which would compound.
 */
export const MuiChipInteractiveLabelExtension = {
  // Already reset to 0 in Tag overrides — no extra rule needed.
  // Keeping as explicit extension point for future chip-role adjustments.
} as const;

// ────────────────────────────────────────────────────────────────────────────
// Combined export for theme integration.
//
// Usage in nsLightTheme.ts:
//   import { MuiChipLightOverrides } from '../components/tag/mui-chip-as-tag-overrides';
//   import { MuiChipInteractiveRootExtension } from '../components/chip/mui-chip-overrides';
//
//   MuiChip: {
//     ...MuiChipLightOverrides,
//     root: {
//       ...MuiChipLightOverrides.root,
//       ...MuiChipInteractiveRootExtension,
//     },
//   }
// ────────────────────────────────────────────────────────────────────────────
